import { useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import CachedIcon from "@mui/icons-material/Cached";
import DoneIcon from "@mui/icons-material/Done";
import styled from "@emotion/styled";
import { useMutation } from "react-query";
import { putResubscribeOrderbook } from "src/Api/exchanges";
import { toast } from "react-toastify";
import { useInterval } from "src/Common";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Menu,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// Static list of known exchanges
const KNOWN_EXCHANGES = [
  { id: "binance", name: "Binance" },
  { id: "kraken", name: "Kraken" },
];

type Exchange = { id: string; name: string };

enum ResubscribeState {
  IDLE = "idle",
  SUCCESS = "success",
  ERROR = "error",
}

const { IDLE, ERROR, SUCCESS } = ResubscribeState;

export const ResubscribeOrderbook = () => {
  const [state, setState] = useState<ResubscribeState>(IDLE);
  const [exchangesToResubscribe, setExchangesToResubscribe] = useState<
    Exchange[]
  >([]);

  const exchanges = KNOWN_EXCHANGES;
  const loadingExchanges = false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { mutateAsync, isLoading } = useMutation(() => {
    const exchangesIds = exchangesToResubscribe.map((e) => e.id);

    return Promise.allSettled([
      putResubscribeOrderbook(exchangesIds.length ? exchangesIds : undefined),
    ]).then((values) => {
      const errorResponse = values.filter(
        (v) => v.status === "rejected"
      ) as PromiseRejectedResult[];

      if (errorResponse.length) {
        setState(ERROR);
        errorResponse.forEach((value) => {
          toast(`Resubscribe Error ${value?.reason?.message}`, {
            theme: "dark",
            type: "error",
            autoClose: 8000,
            pauseOnFocusLoss: false,
          });
        });
      } else {
        toast(`Successful resubscribed`, {
          theme: "dark",
          type: "success",
          autoClose: 8000,
          pauseOnFocusLoss: false,
        });
        setState(SUCCESS);
      }
    });
  });

  useInterval(
    () => {
      setState(IDLE);
    },
    state !== IDLE ? 15000 : null
  );

  const getProps = () => {
    const byState = {
      [IDLE]: {
        startIcon: <CachedIcon />,
        color: "primary",
        variant: "outlined",
      },
      [ERROR]: {
        startIcon: <CachedIcon />,
        color: "error",
        variant: "contained",
      },
      [SUCCESS]: {
        startIcon: <DoneIcon />,
        color: "success",
        variant: "contained",
      },
    };

    return byState[state];
  };

  return (
    <>
      <ButtonGroup>
        <ResubscribeButtonStyled
          loading={isLoading}
          size="small"
          loadingPosition="start"
          {...getProps()}
          onClick={() => {
            mutateAsync();
            handleClose();
          }}
        >
          Resubscribe
        </ResubscribeButtonStyled>

        <ResubscribeButtonOptions
          color="primary"
          size="small"
          variant="outlined"
          onClick={handleClick}
        >
          <Tooltip title={"Select exchanges"} placement="bottom">
            <ArrowDropDownIcon />
          </Tooltip>
        </ResubscribeButtonOptions>
      </ButtonGroup>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Box p="1rem 1rem 1rem 1rem" display="flex" flexDirection="column">
          <Typography variant="body1" mb=".5rem">
            Resubscribe
          </Typography>
          <Autocomplete
            multiple
            loading={loadingExchanges}
            onChange={(_, newValue) => setExchangesToResubscribe(newValue)}
            value={exchangesToResubscribe}
            style={{ width: 400, paddingTop: ".5rem" }}
            disableCloseOnSelect
            id="exchanges-to-resubscribe"
            options={exchanges ?? []}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  avatar={
                    <ExchangeIcon
                      style={{ height: "1rem", width: "1rem" }}
                      name={option.name}
                    />
                  }
                  size="small"
                  label={option.name}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderOption={(props, option) => (
              <Box
                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                component="li"
                {...props}
              >
                <ExchangeIcon style={{ height: "1.5rem" }} name={option.id} />
                <span>{option.name}</span>
              </Box>
            )}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                label="Exchanges to resubscribe"
                {...params}
              />
            )}
          />
          <Box pt="1rem" alignSelf="flex-end">
            <Button variant="outlined" size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              style={{ marginLeft: "0.5rem" }}
              variant="contained"
              size="small"
              disabled={!exchangesToResubscribe.length}
              onClick={() => {
                mutateAsync();
                handleClose();
              }}
            >
              Resubscribe ( {exchangesToResubscribe?.length ?? 0} )
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

const ResubscribeButtonStyled = styled(LoadingButton)((props: any) => ({
  borderRadius: "20rem",
  padding: "0.2rem 1rem 0.2rem 1rem",
  color: "white",
  borderColor: props.theme.palette.grey[600],
  borderWidth: "0.5px",
  fontSize: ".6rem",
  whiteSpace: "nowrap",
}));

const ResubscribeButtonOptions = styled(LoadingButton)((props: any) => ({
  borderRadius: "20rem",
  padding: 0,
  color: "white",
  borderColor: props.theme.palette.grey[600],
  borderWidth: "0.5px",
  fontSize: ".6rem",
  whiteSpace: "nowrap",
}));
